import NativeAdInitialProvider from './NativeAdInitialProvider';
import NativeAdPlacement from './NativeAdPlacement';
import { CustomData, logUsage } from 'owa-analytics';
import { FocusedOtherBitFlagsMasks } from 'owa-bit-flags/lib/utils/focusedOtherBitFlagConstants';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isConsumer } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    embargoedMarkets,
    getAdMarketPublishGroupCode,
    IsChildConsumerUser,
    IsPremiumConsumerUser,
    IsShadowMailboxUser,
} from './sharedAdsUtils';
import ConsumerAdsExperimentMode from 'owa-service/lib/contract/ConsumerAdsExperimentMode';

export const BING_SUPPORTED_MARKETS = ['en-us', 'en-gb', 'en-ca', 'en-au'];

let nativeAdInitialProvider: NativeAdInitialProvider;

let nativeAdEnableOrNot: boolean;

let noShowReason: string;

let userMarket: string;

let previousOtherPivot: boolean;

let userConsumerAdsExpMode: ConsumerAdsExperimentMode;

/*
Get the initial Ad provider for the user
As the Bing flight scope is smaller than Prebid/AppNexus, we check Bing flight first
When Prebid and AppNexus overlap, we use Prebid first as AppNexus is a subset of Prebid which usually acts as the backup plan when Prebid has some issues
*/
export function getNativeAdInitialProviderForUser(): NativeAdInitialProvider {
    if (!nativeAdInitialProvider) {
        const userMarket = getAdMarketPublishGroupCode();

        if (
            !isFeatureEnabled('fwk-nativeAds-bingPrebid') &&
            isFeatureEnabled('fwk-nativeAds-bingOneClickOtherInboxOnly') &&
            BING_SUPPORTED_MARKETS.indexOf(userMarket) > -1
        ) {
            nativeAdInitialProvider = NativeAdInitialProvider.Bing;
        } else {
            nativeAdInitialProvider = NativeAdInitialProvider.Prebid;
        }
    }
    return nativeAdInitialProvider;
}

/*
Get the Ad initial placement. The flow is below:
1. Users which are in the following categories do not get native Ads
-- None of the native ads flight is enabled for this user
-- Ad free user
-- Child account
-- In the Ad experiment where Native Ad is not on
-- User is in a non-supported market
2. If the user is on the Ad "All Inbox" experiment, show in AllInbox
3. If the user's Easy Switch Focus off bit is set which means he switches Focus/Other via the Easy switch, we will show the Ad in Inbox when Focus Inbox is turned off
4. For other users (most set), show the Ad in Other pivot only
*/
export function getNativeAdInitialPlacement(isOtherPivot: boolean): NativeAdPlacement {
    if (!getNativeAdEnabledOrNotFromUserProperties(isOtherPivot)) {
        return NativeAdPlacement.None;
    }

    if (
        (!isFeatureEnabled('fwk-nativeAds-honorEasyOtherOffKillSwitch') &&
            !!(
                FocusedOtherBitFlagsMasks.HasUserClickedEasyTurnOffFocusedInbox &
                getUserConfiguration().ViewStateConfiguration.FocusedInboxBitFlags
            )) ||
        (!isFeatureEnabled('fwk-ads-exp2-RuntimeBypass') &&
            (userConsumerAdsExpMode == 'DisplayOffNativeInbox' ||
                userConsumerAdsExpMode == 'DisplayOnNativeInbox'))
    ) {
        return NativeAdPlacement.OtherPrefer;
    } else {
        return NativeAdPlacement.OtherOnly;
    }
}

/*
Some checks are on the user which will never change during an OWA session
Cache this value so that it will not be calculated again and again
*/
export function getNativeAdEnabledOrNotFromUserProperties(isOtherPivot: boolean): boolean {
    if (typeof nativeAdEnableOrNot != 'undefined') {
        // Only log the data when the user is in the other pivot
        // When the other pivot is clicked, the property change might be triggered several times. We only log the data when the pivot changes (from All to other)
        if (isOtherPivot && !previousOtherPivot) {
            logAdShowOrNoShowData(true);
        }

        previousOtherPivot = isOtherPivot;

        return nativeAdEnableOrNot;
    }

    nativeAdEnableOrNot = false;

    // Do not log the business user session to decrease the noise in the datapoint
    if (!isConsumer()) {
        return nativeAdEnableOrNot;
    }

    userMarket = getAdMarketPublishGroupCode();

    if (
        !isFeatureEnabled('fwk-nativeAds-bingOneClickOtherInboxOnly') &&
        !isFeatureEnabled('fwk-nativeAds-prebidOneClickOtherInboxOnly') &&
        !isFeatureEnabled('fwk-nativeAds-appNexusOneClickOtherInboxOnly')
    ) {
        noShowReason = 'AdFlightNotEnabled';
        logAdShowOrNoShowData(false);
        return nativeAdEnableOrNot;
    }

    if (IsPremiumConsumerUser()) {
        noShowReason = 'PremiumConsumerUser';
        logAdShowOrNoShowData(false);
        return nativeAdEnableOrNot;
    }

    if (IsShadowMailboxUser()) {
        noShowReason = 'shadowMailbox';
        logAdShowOrNoShowData(false);
        return nativeAdEnableOrNot;
    }

    if (IsChildConsumerUser()) {
        noShowReason = 'ChildAccount';
        logAdShowOrNoShowData(false);
        return nativeAdEnableOrNot;
    }

    if (embargoedMarkets.indexOf(userMarket) > -1) {
        noShowReason = 'EmbargoedMarket';
        logAdShowOrNoShowData(false);
        return nativeAdEnableOrNot;
    }

    userConsumerAdsExpMode = getUserConfiguration().UserOptions.ConsumerAdsExperimentMode;

    if (
        !isFeatureEnabled('fwk-ads-exp2-RuntimeBypass') &&
        (userConsumerAdsExpMode == 'DisplayOffNativeOff' ||
            userConsumerAdsExpMode == 'DisplayOnNativeOff')
    ) {
        noShowReason = 'AdsExp2NativeOff';
        logAdShowOrNoShowData(false);
        return nativeAdEnableOrNot;
    }

    nativeAdEnableOrNot = true;
    logAdShowOrNoShowData(false);

    return nativeAdEnableOrNot;
}

// This is only used for test
export function resetNativeAdCacheValueForTest() {
    nativeAdInitialProvider = undefined;
    nativeAdEnableOrNot = undefined;
}

function logAdShowOrNoShowData(isOtherPivot: boolean) {
    // Log the evaluated to show (usually after the OWA boots)
    // Log the actual to show in the other pivot. We do not log actualShow true here as we will log it in the ads module
    // The reason is even it is evaluated to show true here, ad blocker might block the ads file later
    let customData: CustomData;

    customData = { adMarket: userMarket, OtherPivot: isOtherPivot };
    if (!nativeAdEnableOrNot || !isOtherPivot) {
        customData.evaluatedShow = !isOtherPivot ? nativeAdEnableOrNot : null;
        customData.evaluatedNoShowReason = !isOtherPivot ? noShowReason || 'EvaluatedToShow' : null;
        customData.actualShow = isOtherPivot && !nativeAdEnableOrNot ? nativeAdEnableOrNot : null;
        customData.actualNoShowReason = isOtherPivot && !nativeAdEnableOrNot ? noShowReason : null;
        logUsage('ShowNativeAd', customData);
    }
}
